import { API_BASE } from '../config';
const FEED_INITIAL = 3;
const FEED_PREFETCH = 18;
const HISTORY_INITIAL = 3;
const SESSION_KEY = 'match_session_id';
const LOCAL_VIEWED_IDS_KEY = 'feed_viewed_ids';
const LOCAL_HISTORY_KEY = 'feed_recent_history';
const LOCAL_HISTORY_MAX = 60;

const state = {
  promise: null,
  data: null,
  error: null,
  sessionId: null,
  scope: null,
  runtimeSnapshot: null
};

const decodeJwtPayload = (token) => {
  try {
    const payload = token?.split('.')?.[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

const getCurrentUserScope = () => {
  const token = localStorage.getItem('token');
  if (!token) return 'guest';

  const payload = decodeJwtPayload(token);
  return String(
    payload?.sub ??
    payload?.nameid ??
    payload?.['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] ??
    'guest'
  );
};

const buildScopedKey = (baseKey) => `${baseKey}:${getCurrentUserScope()}`;

const resetScopedState = () => {
  state.promise = null;
  state.data = null;
  state.error = null;
  state.sessionId = null;
  state.runtimeSnapshot = null;
};

const ensureScopeState = () => {
  const currentScope = getCurrentUserScope();
  if (state.scope !== currentScope) {
    resetScopedState();
    state.scope = currentScope;
  }
  return currentScope;
};

const getSessionId = () => {
  ensureScopeState();
  return state.sessionId || localStorage.getItem(buildScopedKey(SESSION_KEY));
};

const setSessionId = (sessionId) => {
  ensureScopeState();
  state.sessionId = sessionId || null;
  const sessionKey = buildScopedKey(SESSION_KEY);
  if (sessionId) {
    localStorage.setItem(sessionKey, sessionId);
  } else {
    localStorage.removeItem(sessionKey);
  }
};

const filterHidden = (items, hidden) => {
  if (!Array.isArray(items)) return [];
  if (!Array.isArray(hidden) || hidden.length === 0) return items;
  return items.filter((item) => !hidden.includes(item.id));
};

const readJsonArray = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getLocalViewedIds = () => (
  readJsonArray(buildScopedKey(LOCAL_VIEWED_IDS_KEY))
    .map((id) => String(id || '').trim())
    .filter(Boolean)
);

const getLocalRecentHistory = () => (
  uniqueById(readJsonArray(buildScopedKey(LOCAL_HISTORY_KEY)).map(normalizeCandidate))
);

const uniqueById = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const normalized = normalizeCandidate(item);
    const id = normalized?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(normalized);
  }
  return out;
};

const normalizeCandidate = (item) => {
  if (!item) return item;
  return {
    ...item,
    id: item.id ?? item.Id,
    firstName: item.firstName ?? item.FirstName ?? '',
    lastName: item.lastName ?? item.LastName ?? '',
    nickname: item.nickname ?? item.Nickname ?? '',
    bio: item.bio ?? item.Bio,
    city: item.city ?? item.City,
    jobTitle: item.jobTitle ?? item.JobTitle,
    avatarUrl: item.avatarUrl ?? item.AvatarUrl,
    answers: item.answers ?? item.Answers ?? [],
    matchPercentage: item.matchPercentage ?? item.MatchPercentage,
    isLikedByMe: item.isLikedByMe ?? item.IsLikedByMe ?? false,
    isMutual: item.isMutual ?? item.IsMutual ?? false
  };
};

const buildBatchUrl = (count, sessionId) => {
  const params = new URLSearchParams({ count: String(count) });
  if (sessionId) params.set('sessionId', sessionId);
  return `${API_BASE}/api/Matching/batch?${params.toString()}`;
};

const fetchBatchWithSessionFallback = async ({ count, sessionId, headers }) => {
  const firstResp = await fetch(buildBatchUrl(count, sessionId), { headers });
  const firstSessionId = firstResp.headers.get('X-Match-Session');
  if (firstSessionId) setSessionId(firstSessionId);

  if (!firstResp.ok) {
    return { response: firstResp, items: null };
  }

  const firstData = await firstResp.json();
  const firstItems = Array.isArray(firstData) ? firstData : [];

  // Session can be exhausted (all ids already in seen list). Retry once with a fresh session.
  if (firstItems.length === 0 && sessionId) {
    setSessionId(null);
    const retryResp = await fetch(buildBatchUrl(count, null), { headers });
    const retrySessionId = retryResp.headers.get('X-Match-Session');
    if (retrySessionId) setSessionId(retrySessionId);

    if (!retryResp.ok) {
      return { response: retryResp, items: null };
    }

    const retryData = await retryResp.json();
    return { response: retryResp, items: Array.isArray(retryData) ? retryData : [] };
  }

  return { response: firstResp, items: firstItems };
};

const loadFeedData = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    resetScopedState();
    setSessionId(null);
    return null;
  }

  ensureScopeState();

  const headers = { 'Authorization': `Bearer ${token}` };
  const hidden = JSON.parse(localStorage.getItem('hidden_candidates') || '[]');
  const sessionId = getSessionId();
  const localViewedIds = getLocalViewedIds();
  const localRecentHistory = getLocalRecentHistory();

  try {
    const [feedResult, historyResp] = await Promise.all([
      fetchBatchWithSessionFallback({ count: FEED_INITIAL, sessionId, headers }),
      fetch(`${API_BASE}/api/Matching/history?limit=${HISTORY_INITIAL}&offset=0`, { headers })
    ]);
    const feedResp = feedResult.response;

    if (feedResp.status === 401 || historyResp.status === 401) {
      localStorage.removeItem('token');
      setSessionId(null);
      resetScopedState();
      return null;
    }

    const activeSessionId = getSessionId();

    if (!feedResp.ok) throw new Error('batch fetch failed');

    const feedInitial = uniqueById(filterHidden(feedResult.items, hidden));

    let historyItems = [];
    let historyOffset = 0;

    if (historyResp.ok) {
      const historyData = await historyResp.json();
      historyItems = Array.isArray(historyData.items) ? uniqueById(historyData.items.slice().reverse()) : [];
      historyOffset = historyData.nextOffset ?? historyItems.length;
    }

    const localRecentHistoryIds = new Set(localRecentHistory.map((item) => item.id));
    historyItems = uniqueById(
      historyItems
        .filter((item) => !localRecentHistoryIds.has(item.id))
        .concat(localRecentHistory)
    );

    let feedExtra = [];
    try {
      const moreResult = await fetchBatchWithSessionFallback({
        count: FEED_PREFETCH,
        sessionId: activeSessionId,
        headers
      });
      const moreResp = moreResult.response;
      if (moreResp.status === 401) {
        localStorage.removeItem('token');
        setSessionId(null);
        resetScopedState();
        return null;
      }
      if (moreResp.ok) {
        feedExtra = uniqueById(filterHidden(moreResult.items, hidden));
      }
    } catch (e) {
      // ignore preload errors
    }

    const historyIdSet = new Set(historyItems.map((x) => x.id));
    const locallyViewedSet = new Set(localViewedIds);
    const mergedFeed = uniqueById(feedInitial.concat(feedExtra))
      .filter((item) => !historyIdSet.has(item.id) && !locallyViewedSet.has(item.id));

    const data = {
      feed: mergedFeed,
      history: historyItems,
      historyOffset
    };

    state.data = data;
    return data;
  } catch (e) {
    state.error = e;
    state.data = null;
    state.promise = null;
    return null;
  }
};

export const primeFeedData = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    resetScopedState();
    setSessionId(null);
    return Promise.resolve(null);
  }

  ensureScopeState();

  if (state.promise) return state.promise;

  state.promise = loadFeedData();
  return state.promise;
};

export const getPrefetchedFeed = () => state.data;

export const getMatchSessionId = () => getSessionId();

export const setMatchSessionId = (sessionId) => setSessionId(sessionId);

export const clearFeedPrefetch = () => {
  resetScopedState();
};

export const getFeedRuntimeSnapshot = () => {
  ensureScopeState();
  return state.runtimeSnapshot;
};

export const setFeedRuntimeSnapshot = (snapshot) => {
  ensureScopeState();
  state.runtimeSnapshot = snapshot || null;
};

export const syncPrefetchedDataAfterViewed = (candidate) => {
  ensureScopeState();

  const normalized = normalizeCandidate(candidate);
  const id = normalized?.id;
  if (!id || !state.data) return;

  const currentHistory = Array.isArray(state.data.history) ? state.data.history : [];
  const currentFeed = Array.isArray(state.data.feed) ? state.data.feed : [];

  const nextHistory = uniqueById(
    currentHistory
      .filter((item) => item?.id !== id)
      .concat(normalized)
  );

  const nextFeed = currentFeed.filter((item) => item?.id !== id);

  state.data = {
    ...state.data,
    history: nextHistory,
    feed: nextFeed
  };
};

export const recordLocalViewedCandidate = (candidate) => {
  const normalized = normalizeCandidate(candidate);
  const id = normalized?.id;
  if (!id) return;

  const nextViewedIds = getLocalViewedIds().filter((item) => item !== id);
  nextViewedIds.push(id);
  localStorage.setItem(buildScopedKey(LOCAL_VIEWED_IDS_KEY), JSON.stringify(nextViewedIds));

  const nextHistory = getLocalRecentHistory().filter((item) => item?.id !== id);
  nextHistory.push(normalized);
  localStorage.setItem(buildScopedKey(LOCAL_HISTORY_KEY), JSON.stringify(nextHistory.slice(-LOCAL_HISTORY_MAX)));
  syncPrefetchedDataAfterViewed(normalized);
};

export const removeLocalViewedCandidates = (ids) => {
  const blocked = new Set((Array.isArray(ids) ? ids : []).map((id) => String(id || '').trim()).filter(Boolean));
  if (blocked.size === 0) return;

  const nextViewedIds = getLocalViewedIds().filter((id) => !blocked.has(id));
  const nextHistory = getLocalRecentHistory().filter((item) => !blocked.has(item?.id));
  localStorage.setItem(buildScopedKey(LOCAL_VIEWED_IDS_KEY), JSON.stringify(nextViewedIds));
  localStorage.setItem(buildScopedKey(LOCAL_HISTORY_KEY), JSON.stringify(nextHistory));
};
